<template>
  <IndicatorCard
    :loading="loading"
    :has-data="data.dataByMonth.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum equipamento encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12" sm="7">
        <BaseEChart :option="barOption" :loading="loading" height="320px" />
      </v-col>
      <v-col cols="12" sm="5">
        <BaseEChart :option="gaugeOption" :loading="loading" height="280px" />
      </v-col>
      <v-col cols="12">
        <v-simple-table class="main-table" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left"></th>
                <th class="text-left"></th>
                <th
                  class="text-center"
                  v-for="month in data.dataByMonth.map((x) => x.month)"
                  :key="month"
                >
                  {{ month }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="max-width: 60px"></td>
                <td style="max-width: 60px">{{ $t('Internas') }}</td>
                <td
                  v-for="item in data.dataByMonth"
                  :key="item.month"
                  class="text-center"
                >
                  {{ item.totalInternal }}
                </td>
              </tr>
              <tr>
                <td style="max-width: 60px"></td>
                <td style="max-width: 60px">{{ $t('Externas') }}</td>
                <td
                  v-for="item in data.dataByMonth"
                  :key="item.month"
                  class="text-center"
                >
                  {{ item.totalServiceOrders - item.totalInternal }}
                </td>
              </tr>
              <tr style="background: #0288d1">
                <td style="max-width: 60px"></td>
                <td style="max-width: 60px; color: white !important">
                  {{ $t('Porcentagem') }}
                </td>
                <td
                  style="color: white !important"
                  v-for="item in data.dataByMonth"
                  :key="item.month"
                  class="text-center"
                >
                  {{ `${item.percentage}%` }}
                </td>
              </tr>
              <tr style="background: orange">
                <td style="max-width: 60px"></td>
                <td style="max-width: 60px; color: white !important">
                  {{ $t('Meta') }}
                </td>
                <td
                  style="color: white !important"
                  v-for="item in data.dataByMonth"
                  :key="item.month"
                  class="text-center"
                >
                  {{ `${data.goal}%` }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildBarOption, buildGaugeOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  data() {
    return {
      excelLoading: false,
      extensions: [
        History, Blockquote, Link, Underline, Strike, Italic, ListItem,
        BulletList, OrderedList,
        [Heading, { options: { levels: [1, 2, 3] } }],
        Bold, Code, HorizontalRule, Paragraph, HardBreak,
      ],
      filter: {
        page: 1,
        pageSize: 7,
        institutionId: null,
        unidadeIds: [],
        startDate: null,
        endDate: null,
        review: '',
      },
      startDate: null,
      endDate: null,
      apiService: new ApiService(),
      loading: true,
      total: 0,
      data: {
        dataByMonth: [],
        goal: 0,
      },
    }
  },

  computed: {
    indicator() {
      return getIndicatorById('internal_resolvability') || {}
    },
    overallPercentage() {
      if (!this.data.dataByMonth.length) return 0
      const sum = this.data.dataByMonth.reduce(
        (acc, item) => acc + (item.percentage || 0),
        0
      )
      return parseFloat((sum / this.data.dataByMonth.length).toFixed(1))
    },

    barOption() {
      return buildBarOption({
        categories: this.data.dataByMonth.map((x) => x.month),
        series: [
          {
            name: this.$t('Execução total'),
            data: this.data.dataByMonth.map((x) => x.percentage),
            color: '#0288d1',
          },
        ],
        goal: this.data.goal,
        percent: true,
        showLegend: true,
      })
    },

    gaugeOption() {
      return buildGaugeOption({
        value: this.overallPercentage,
        goal: this.data.goal,
        label: 'Resolutividade',
      })
    },
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('internal_resolvability').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          const response = JSON.parse(this.filterProp.data)
          this.data = response.content
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/internalResoluteness',
          this.filter
        )
        this.data = response.content
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
